// Pure helper for the AdvancedMD charge-slip field mapping.
//
// Both charge-slip mappings reduce to the same operation: find the charge-slip line
// by its code (each line is keyed by a clean `data-code` attribute) and write a value
// into one of its fields (Units input, Mod input, …) targeted by an XPath template
// parameterized by `${code}`. The two use cases differ only in where the value comes
// from:
//   • per-item value  — read item[valueProperty] from a list (e.g. cpt2codes.modifier → Units)
//   • fixed value      — write one constant for every code (e.g. "25" → E/M Mod field)
//
// Side-effect free so it can be unit-tested directly and reused by the single
// `setFieldByCode` action in useEHR.js.

import { sanitizeCodes } from "./billingCodesPaste";
import { isCodeValidForLosSlot, normalizeModifier } from "./losCodeParse";
import { isNonBillableList, isNotBilled } from "./notBilled";

const LOS_SLOT_FAMILY = { enmCode: "992", annualCode: "993" };
// eslint-disable-next-line no-control-regex
const UNSAFE_CODE_CHARS = /['"`\[\]()\/\\<>{}=\s\u0000-\u001f]/;

/**
 * @param {*} source                          context-variable value (array of items, or a
 *                                             single value when using fixedValue + treatAsSingle)
 * @param {object} options
 * @param {string}  [options.codeProperty="code"]    property holding the code
 * @param {boolean} [options.treatAsSingle=false]    treat source as one value, not a list (fixed mode),
 *                                                   or a single object as a one-item list (per-item mode)
 * @param {string}  [options.valueProperty]          per-item: property holding the value to write
 * @param {*}       [options.fixedValue]             fixed mode: one constant written for every code
 *                                                   (takes precedence over valueProperty when non-blank)
 * @param {boolean} [options.numericOnly=false]      per-item mode only: only write values that are a
 *                                                   positive finite number (units safety — writing
 *                                                   0/garbage would corrupt a line). Ignored in fixed mode.
 * @param {string}  options.fieldSelectorTemplate    XPath template containing the literal `${code}`
 * @param {string}  [options.valueSource]            the step's list name; the non-billable list writes nothing
 * @returns {Array<{elementType: "path", element: string, value: string, code: string}>}
 *
 * Skips empty codes, blank/invalid values, and duplicate codes (first occurrence wins).
 */
export function buildFieldsByCode(source, options = {}) {
  const {
    codeProperty = "code",
    treatAsSingle = false,
    notBilledText = false,
    valueProperty,
    fixedValue,
    numericOnly = false,
    fieldSelectorTemplate,
    valueSource,
  } = options;

  if (!fieldSelectorTemplate) return [];
  // Never a slip source, even if a mapping step names it. Refused by list name: useEHR
  // forwards the step's valueSource to every slip helper call (src/hooks/__tests__/
  // useEHR.slipValueSource.test.js fails if one stops); sanitizeCodes reports the skip.
  if (isNonBillableList(valueSource)) return [];

  const fixed = fixedValue == null ? "" : String(fixedValue).trim();

  // Normalize both use cases into a flat [{code, value}] list.
  let entries;
  if (fixed !== "") {
    // Fixed-value mode: extract codes (sanitizeCodes handles arrays, {code}/{enmCode}
    // objects, bare strings, and single values via treatAsSingle), apply one constant.
    const { codes } = sanitizeCodes(source, codeProperty || "code", { treatAsSingle, notBilledText });
    entries = codes.map((code) => ({ code, value: fixed }));
  } else {
    // Per-item value mode: read code + value from each object in the list. A single
    // object (the levelOfService {annualCode, annualModifier} shape) is a one-item list
    // only when the step opts in with treatAsSingle.
    let items = source;
    if (!Array.isArray(items)) {
      if (!(treatAsSingle && items && typeof items === "object")) return [];
      items = [items];
    }
    const prop = codeProperty || "code";
    // The levelOfService annual slot gets the same guard the paste step applies
    // (sanitizeCodes): an E/M code sitting in annualCode must never receive the
    // preventive modifier. enmCode mirrors it for the other slot.
    const slotFamily = treatAsSingle ? LOS_SLOT_FAMILY[prop] : undefined;
    // useEHR forwards only a fixed set of options, so the modifier-format rule is keyed
    // off the target field: any per-item write into a Mod input must be modifier-shaped.
    const isModifierField = fieldSelectorTemplate.includes("txtModifier");
    entries = items
      .filter((item) => item && typeof item === "object" && !isNotBilled(item))
      .map((item) => ({
        code: item[prop],
        value: isModifierField ? normalizeModifier(item[valueProperty]) : item[valueProperty],
        slotFamily,
      }));
  }

  const seen = new Set();
  const fields = [];

  for (const entry of entries) {
    if (entry.code == null) continue;
    const code = String(entry.code).trim();
    if (code === "") continue;
    // The code is spliced into an XPath attribute literal: never let a quote, bracket,
    // slash, backslash or angle bracket (or whitespace/control char) reach the selector.
    if (UNSAFE_CODE_CHARS.test(code)) continue;
    if (entry.slotFamily && !isCodeValidForLosSlot(code, entry.slotFamily)) continue;

    let value;
    if (numericOnly && fixed === "") {
      // numericOnly guards per-item data values only; a deliberate fixed constant
      // is written as-is (so e.g. fixedValue "RT" isn't silently dropped).
      const n = Number(String(entry.value ?? "").trim());
      if (!Number.isFinite(n) || n <= 0) continue;
      value = String(n);
    } else {
      value = String(entry.value ?? "").trim();
      if (value === "") continue;
    }

    if (seen.has(code)) continue;
    seen.add(code);

    fields.push({
      elementType: "path",
      // "${code}" is a literal placeholder substituted per code, not a JS template literal.
      // eslint-disable-next-line no-template-curly-in-string
      element: fieldSelectorTemplate.split("${code}").join(code),
      value,
      code,
    });
  }

  return fields;
}

// Literal placeholder substituted per code by buildFieldsByCode. Isolated into its own
// short constant so the lint suppression stays attached to the string.
// eslint-disable-next-line no-template-curly-in-string
const CODE_PLACEHOLDER = "${code}";

/**
 * Turn a fieldSelectorTemplate into an XPath that matches ANY charge-slip line, by
 * dropping the predicate that pins it to one code plus everything after it.
 *
 *   //div[contains(@class,'summaryitem')][@data-code='${code}']//input[…]
 *   → //div[contains(@class,'summaryitem')]
 *
 * Used to tell "the slip never opened" apart from "the slip is open but this action's
 * codes aren't on it" — two situations that both write zero fields but mean very
 * different things to the provider.
 *
 * The result is always a literal prefix of a selector the template already relies on,
 * never a guess: a probe invented out of thin air would fail every run and be strictly
 * worse than no probe at all.
 *
 * @param {string} fieldSelectorTemplate
 * @returns {string|null} the row selector, or null when no meaningful probe can be built
 */
export function deriveSlipRowSelector(fieldSelectorTemplate) {
  if (typeof fieldSelectorTemplate !== "string") return null;

  const codeAt = fieldSelectorTemplate.indexOf(CODE_PLACEHOLDER);
  if (codeAt === -1) return null;

  // Cut at the "[" opening the predicate that holds the code, so the row step survives
  // with any earlier predicates intact.
  const cut = fieldSelectorTemplate.lastIndexOf("[", codeAt);
  if (cut <= 0) return null;

  const prefix = fieldSelectorTemplate.slice(0, cut).trim();
  // Must still be a path (not a bare predicate fragment) and must still narrow: a probe
  // like "//div" matches on any page, so it would silence the real "slip never opened"
  // error instead of testing for it. No probe is better than one that always passes.
  if (!prefix.startsWith("/") && !prefix.startsWith("(")) return null;
  if (!prefix.includes("[")) return null;

  return prefix;
}

// Preset inserted by the "Set Units By Code" entry in the EHR template builder:
// each cpt2codes entry's `modifier` (the units count) → that line's Units input.
export const UNITS_FIELD_PRESET = {
  type: "setFieldByCode",
  description: "Charge slip: set Units = modifier for each cpt2code",
  valueSource: "cpt2codes",
  codeProperty: "code",
  valueProperty: "modifier",
  numericOnly: true,
  fieldSelectorTemplate:
    "//div[contains(@class,'summaryitem')][@data-code='" + CODE_PLACEHOLDER + "']//input[contains(@class,'summarycodeunits')]",
  waitBeforeMs: 800,
  useCDP: false,
};

// Preset inserted by the "Set Modifier By Code" entry: a fixed modifier (25) → the
// level-of-service (E/M) line's Mod field.
export const MODIFIER_FIELD_PRESET = {
  type: "setFieldByCode",
  description: "Charge slip: set Modifier 25 on the level-of-service line",
  valueSource: "levelOfService",
  codeProperty: "enmCode",
  treatAsSingle: true,
  fixedValue: "25",
  fieldSelectorTemplate:
    "//div[contains(@class,'summaryitem')][@data-code='" + CODE_PLACEHOLDER + "']//input[contains(@class,'txtModifier')]",
  waitBeforeMs: 800,
  useCDP: false,
};

// Per-line modifier: each code-list entry's `cptModifier` (25/51/59, decided by the
// backend) → that line's Mod field. Entries without one are skipped, so the fixed-25
// preset above still owns the E/M line. Retarget valueSource to cpt2codes for a second
// step when drug lines carry modifiers too.
export const CPT_MODIFIER_FIELD_PRESET = {
  type: "setFieldByCode",
  description: "Charge slip: set Modifier = cptModifier for each office procedure",
  valueSource: "inOfficeProcedures",
  codeProperty: "code",
  valueProperty: "cptModifier",
  fieldSelectorTemplate:
    "//div[contains(@class,'summaryitem')][@data-code='" + CODE_PLACEHOLDER + "']//input[contains(@class,'txtModifier')]",
  waitBeforeMs: 800,
  useCDP: false,
};

// Office-procedure units: `units` → that line's Units input.
// NOTE: legacy Office Procedures rows keep their units count in `modifier` (the same
// name cpt2codes uses for units). This preset reads only `units`, so a legacy row sends
// NOTHING to the slip until it is re-edited (the units dropdown then writes `units`).
// The note may still show "x N" for such a row via displayUnits' legacy fallback.
export const OP_UNITS_FIELD_PRESET = {
  type: "setFieldByCode",
  description: "Charge slip: set Units = units for each office procedure",
  valueSource: "inOfficeProcedures",
  codeProperty: "code",
  valueProperty: "units",
  numericOnly: true,
  fieldSelectorTemplate:
    "//div[contains(@class,'summaryitem')][@data-code='" + CODE_PLACEHOLDER + "']//input[contains(@class,'summarycodeunits')]",
  waitBeforeMs: 800,
  useCDP: false,
};

// Preventive line modifier: levelOfService.annualModifier (the 25 on a preventive-only
// visit) → the annual code's Mod field. Writes nothing when annualModifier is absent.
export const ANNUAL_MODIFIER_FIELD_PRESET = {
  type: "setFieldByCode",
  description: "Charge slip: set Modifier = annualModifier on the preventive line",
  valueSource: "levelOfService",
  codeProperty: "annualCode",
  treatAsSingle: true,
  valueProperty: "annualModifier",
  fieldSelectorTemplate:
    "//div[contains(@class,'summaryitem')][@data-code='" + CODE_PLACEHOLDER + "']//input[contains(@class,'txtModifier')]",
  waitBeforeMs: 800,
  useCDP: false,
};
